import {ReviewsFilterFieldNameEnum} from '../../page/main/reviews/review-list-page-content/reviews-filter/reviews-filter-type';
import {ReviewsStateType, ReviewsStateUrlQueryNameEnum} from '../../page/main/reviews/reviews-state/reviews-state-type';
import {isValidUrl} from '../../util/url';

import {
    buildRatingSortPageStartSnapshots,
    getEmptyRatingSortPageSnapshot,
    getRatingSortPageStartSnapshot,
    isRatingSortPageStartSnapshotValid,
    loadRatingSortPagination,
    RatingSortPageSnapshotType,
    saveRatingSortPagination,
} from './reviews-rating-sort-pagination-helper';
import {FetchReviewsResultType, ReviewDataType, ReviewSortingTypeEnum} from './reviews-type';

export class RatingSortPaginationBrokenError extends Error {
    constructor() {
        super('Rating sort pagination state is broken');
        this.name = 'RatingSortPaginationBrokenError';
    }
}

const ALL_RATINGS = [5, 4, 3, 2, 1, 0];

function getRatingBuckets(sort: ReviewSortingTypeEnum, ratingList: Array<number>): Array<number> {
    const effectiveRatings = ratingList.length > 0 ? ratingList : ALL_RATINGS;
    const orderedRatings = sort === ReviewSortingTypeEnum.ratingDesc ? ALL_RATINGS : [...ALL_RATINGS].reverse();

    return orderedRatings.filter((rating) => effectiveRatings.includes(rating));
}

function extractCursorFromResponseUrl(cursorResponse: string | null | void): string {
    if (!cursorResponse) {
        return '';
    }

    if (isValidUrl(cursorResponse)) {
        return new URL(cursorResponse).searchParams.get('cursor') || '';
    }

    return cursorResponse;
}

type FetchRatingBucketType = (rating: number, pageSize: number, cursor: string) => Promise<FetchReviewsResultType>;

type FetchReviewsWithRatingSortOptionsType = {
    reviewsState: ReviewsStateType;
    pageSize: number;
    page: number;
    storageKey: string;
    fetchRatingBucket: FetchRatingBucketType;
};

type FetchReviewsWithRatingSortResultType = {
    result: FetchReviewsResultType;
    hasNextPage: boolean;
};

type FillRatingSortPageResultType = {
    results: Array<ReviewDataType>;
    endSnapshot: RatingSortPageSnapshotType;
    hasNextPage: boolean;
};

function splitBuffer(
    buffer: Array<ReviewDataType>,
    count: number
): {taken: Array<ReviewDataType>; rest: Array<ReviewDataType>} {
    return {
        taken: buffer.slice(0, count),
        rest: buffer.slice(count),
    };
}

function hasMoreReviewsAfterPage(
    buckets: Array<number>,
    snapshot: RatingSortPageSnapshotType,
    bucketMayHaveMore: boolean
): boolean {
    if (snapshot.buffer.length > 0) {
        return true;
    }

    if (bucketMayHaveMore) {
        return true;
    }

    if (snapshot.bucketIndex >= buckets.length) {
        return false;
    }

    const currentRating = buckets[snapshot.bucketIndex] as number;
    const ratingKey = String(currentRating);

    if (snapshot.bucketCursors[ratingKey]) {
        return true;
    }

    return snapshot.bucketIndex < buckets.length;
}

function mergeBucketBatch(options: {
    batch: Array<ReviewDataType>;
    pageSize: number;
    snapshot: RatingSortPageSnapshotType;
    results: Array<ReviewDataType>;
    ratingKey: string;
    nextCursor: string;
}): {
    newResults: Array<ReviewDataType>;
    newSnapshot: RatingSortPageSnapshotType;
    isPageFull: boolean;
    bucketMayHaveMore: boolean;
} {
    const {batch, pageSize, snapshot, results, ratingKey, nextCursor} = options;
    const remaining = pageSize - results.length;
    const taken = batch.slice(0, remaining);
    const surplus = batch.slice(remaining);
    const bucketExhausted = batch.length < pageSize || !nextCursor;
    const updatedCursors = {...snapshot.bucketCursors};

    if (bucketExhausted) {
        Reflect.deleteProperty(updatedCursors, ratingKey);
    } else {
        updatedCursors[ratingKey] = nextCursor;
    }

    return {
        newResults: [...results, ...taken],
        newSnapshot: {
            buffer: [...snapshot.buffer, ...surplus],
            bucketCursors: updatedCursors,
            bucketIndex: bucketExhausted ? snapshot.bucketIndex + 1 : snapshot.bucketIndex,
        },
        isPageFull: results.length + taken.length >= pageSize,
        bucketMayHaveMore: Boolean(nextCursor),
    };
}

async function fillResultsFromBuckets(
    buckets: Array<number>,
    pageSize: number,
    snapshot: RatingSortPageSnapshotType,
    results: Array<ReviewDataType>,
    fetchRatingBucket: FetchRatingBucketType
): Promise<FillRatingSortPageResultType> {
    if (results.length >= pageSize) {
        return {
            results,
            endSnapshot: snapshot,
            hasNextPage: hasMoreReviewsAfterPage(buckets, snapshot, false),
        };
    }

    if (snapshot.bucketIndex >= buckets.length) {
        return {
            results,
            endSnapshot: snapshot,
            hasNextPage: false,
        };
    }

    const rating = buckets[snapshot.bucketIndex] as number;
    const ratingKey = String(rating);
    const cursor = snapshot.bucketCursors[ratingKey] ?? '';
    const response = await fetchRatingBucket(rating, pageSize, cursor);
    const batch = response.results;
    const nextCursor = extractCursorFromResponseUrl(response.next);

    if (batch.length === 0) {
        return fillResultsFromBuckets(
            buckets,
            pageSize,
            {
                ...snapshot,
                bucketIndex: snapshot.bucketIndex + 1,
            },
            results,
            fetchRatingBucket
        );
    }

    const {newResults, newSnapshot, isPageFull, bucketMayHaveMore} = mergeBucketBatch({
        batch,
        pageSize,
        snapshot,
        results,
        ratingKey,
        nextCursor,
    });

    if (isPageFull) {
        return {
            results: newResults,
            endSnapshot: newSnapshot,
            hasNextPage: hasMoreReviewsAfterPage(buckets, newSnapshot, bucketMayHaveMore),
        };
    }

    return fillResultsFromBuckets(buckets, pageSize, newSnapshot, newResults, fetchRatingBucket);
}

async function fillRatingSortPage(
    buckets: Array<number>,
    pageSize: number,
    startSnapshot: RatingSortPageSnapshotType,
    fetchRatingBucket: FetchRatingBucketType
): Promise<FillRatingSortPageResultType> {
    const {taken, rest} = splitBuffer(startSnapshot.buffer, pageSize);

    if (taken.length >= pageSize) {
        const endSnapshot: RatingSortPageSnapshotType = {
            ...startSnapshot,
            buffer: rest,
        };

        return {
            results: taken,
            endSnapshot,
            hasNextPage: hasMoreReviewsAfterPage(buckets, endSnapshot, false),
        };
    }

    return fillResultsFromBuckets(
        buckets,
        pageSize,
        {
            ...startSnapshot,
            buffer: rest,
        },
        taken,
        fetchRatingBucket
    );
}

export async function fetchReviewsWithRatingSort(
    options: FetchReviewsWithRatingSortOptionsType
): Promise<FetchReviewsWithRatingSortResultType> {
    const {reviewsState, pageSize, page, storageKey, fetchRatingBucket} = options;
    const ratingList = reviewsState.filterState[ReviewsFilterFieldNameEnum.ratingList];
    const buckets = getRatingBuckets(reviewsState[ReviewsStateUrlQueryNameEnum.sorting], ratingList);

    const store = loadRatingSortPagination(storageKey);

    if (!isRatingSortPageStartSnapshotValid(store, page)) {
        throw new RatingSortPaginationBrokenError();
    }

    const startSnapshot = getRatingSortPageStartSnapshot(store, page);
    const {results, endSnapshot, hasNextPage} = await fillRatingSortPage(
        buckets,
        pageSize,
        startSnapshot,
        fetchRatingBucket
    );

    const previousSnapshots = store?.pageStartSnapshots || [getEmptyRatingSortPageSnapshot()];

    saveRatingSortPagination(storageKey, {
        pageStartSnapshots: buildRatingSortPageStartSnapshots(previousSnapshots, page, endSnapshot),
        hasNextPage,
    });

    return {
        result: {
            results,
            next: hasNextPage ? String(page + 1) : null,
            previous: page > 1 ? String(page - 1) : null,
        },
        hasNextPage,
    };
}
